import _ from "lodash";
import {
  UserProfileOutlined,
  EyeOutlined,
  SettingsOutlined,
  GraduationCapOutlined,
  CloseCircleOutlined,
  TeacherResourceOutlined,
} from "@toddle-edu/ds-icons";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
} from "Constants/stringConstants";
import {
  PROJECT_GROUP_TYPE_COMMUNITY,
  PROJECT_GROUP_TYPE_PERSONAL,
  PROJECT_GROUP_TYPE_SERVICE_AS_ACTON,
  PROJECT_GROUP_TYPE_DP_CAS,
  PROJECT_GROUP_TYPE_DP_EE,
  PROJECT_GROUP_TYPE_DP_TOK_ESSAY,
  PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION,
  PROJECT_GROUP_TYPE_DP_G4_PROJECT,
  PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT,
  PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT,
  PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING,
  PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT,
} from "Projects/Constants/stringConstants";

export const RULE_OPTIONS = {
  gt: "gt",
  gte: "gte",
  eq: "eq",
  lt: "lt",
  lte: "lte",
};

export const ACCESS_LEVELS = {
  ADMIN_ACCESS: {
    id: "ADMIN_ACCESS",
    labelLocale: "configuration:staff_perm_admin_access_label",
    Icon: SettingsOutlined,
  },
  TEACHER_ACCESS: {
    id: "TEACHER_ACCESS",
    labelLocale: "configuration:staff_perm_teacher_access_label",
    Icon: UserProfileOutlined,
  },
  CREATOR_ACCESS: {
    id: "CREATOR_ACCESS",
    labelLocale: "configuration:staff_perm_creator_label",
    Icon: TeacherResourceOutlined,
  },
  VIEW_ACCESS: {
    id: "VIEW_ACCESS",
    labelLocale: "configuration:staff_perm_view_access_label",
    Icon: EyeOutlined,
  },
  NO_ACCESS: {
    id: "NO_ACCESS",
    labelLocale: "configuration:staff_perm_no_access_label",
    Icon: CloseCircleOutlined,
  },
  COORDINATOR_ACCESS: {
    id: "COORDINATOR_ACCESS",
    labelLocale: "configuration:staff_perm_coordinator_label",
    Icon: GraduationCapOutlined,
  },
};

export const MODULES = {
  ATTENDANCE: {
    id: "ATTENDANCE",
    label: "common:attendance",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:AttendanceAvailability",
    modulePerm: "TeacherPortal:Attendance",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_attendance_coordinator_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_attendance_teacher_access_desc",
      },
    },
  },
  ANNOUNCEMENTS: {
    id: "ANNOUNCEMENTS",
    label: "announcement:announcements",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:AnnouncementAvailability",
    modulePerm: "Common:Announcement",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_announcements_coordinator_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_announcements_teacher_access_desc",
      },
      VIEW_ACCESS: {
        ...ACCESS_LEVELS.VIEW_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_announcements_restricted_access_desc",
      },
    },
  },
  EVALUATION_CYCLES: {
    id: "EVALUATION_CYCLES",
    label: "common:evaluation_cycles",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:EvaluationCyclesAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_evaluation_cycle_coordinator_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_evaluation_cycle_teacher_access_desc",
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_evaluation_cycle_no_access_desc",
      },
    },
  },
  FAMILY_COMMUNICATION: {
    id: "FAMILY_COMMUNICATION",
    label: "teacherHomePage:family_communication_sentence_case",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:FamilyMessagingAvailability",
    modulePerm: "TeacherPortal:FamilyCommunications",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_family_communication_coordinator_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_family_communication_teacher_access_desc",
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_family_communication_no_access_desc",
      },
    },
  },
  PROGRESS_REPORTS: {
    id: "PROGRESS_REPORTS",
    label: "teacherHomePage:progress_report_sentence_case",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:ProgressReportsAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_progress_reports_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_progress_reports_teacher_access_desc",
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_progress_reports_no_access_desc",
      },
    },
  },
  STUDENT_PROFILE: {
    id: "STUDENT_PROFILE",
    label: "teacherHomePage:student_profile_sentence_case",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:StudentProfileAvailability",
    modulePerm: "TeacherPortal:StudentPortfolio",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_student_profile_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_student_profile_teacher_access_desc",
      },
      VIEW_ACCESS: {
        ...ACCESS_LEVELS.VIEW_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_student_profile_restricted_access_desc",
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_student_profile_no_access_desc",
      },
    },
  },
  CLASS_SETTINGS: {
    id: "CLASS_SETTINGS",
    label: "common:class_settings_sentence_case",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    perm: "StaffPermission:ClassSettingsAvailability",
    values: {
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_class_settings_teacher_access_desc",
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_class_settings_no_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_PERSONAL]: {
    id: PROJECT_GROUP_TYPE_PERSONAL,
    label: "project:personal_project",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:PersonalProjectAvailability",
    moduleAvailabilityPerm: "Common:PersonalProject",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_personal_project_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_personal_project_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_COMMUNITY]: {
    id: PROJECT_GROUP_TYPE_COMMUNITY,
    label: "project:community_project",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:CommunityProjectAvailability",
    moduleAvailabilityPerm: "Common:CommunityProject",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_community_project_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_community_project_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_SERVICE_AS_ACTON]: {
    id: PROJECT_GROUP_TYPE_SERVICE_AS_ACTON,
    label: "project:service_as_action_sentence_case",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:ServiceAsActionAvailability",
    moduleAvailabilityPerm: "Common:ServiceAsAction",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_service_as_action_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_service_as_action_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_DP_EE]: {
    id: PROJECT_GROUP_TYPE_DP_EE,
    label: "project:extended_essay",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:ExtendedEssayAvailability",
    moduleAvailabilityPerm: "Common:EEProjects",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_extended_essay_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_extended_essay_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT]: {
    id: PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT,
    label: "project:reflective_project",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:ReflectiveProjectAvailability",
    moduleAvailabilityPerm: "Common:ReflectiveProject",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_reflective_project_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_reflective_project_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_DP_TOK_ESSAY]: {
    id: PROJECT_GROUP_TYPE_DP_TOK_ESSAY,
    label: "project:tok_essay_sentence_case",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:TOKEssayAvailability",
    moduleAvailabilityPerm: "Common:TOKEssayProjects",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_tok_essay_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_tok_essay_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION]: {
    id: PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION,
    label: "project:tok_exhibition_sentence_case",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:TOKExhibitionAvailability",
    moduleAvailabilityPerm: "Common:TOKExhibitionProjects",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_tok_exhibition_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_tok_exhibition_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_DP_CAS]: {
    id: PROJECT_GROUP_TYPE_DP_CAS,
    label: "project:creativity_action_service",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:CreativityActivityServiceAvailability",
    moduleAvailabilityPerm: "Common:CASPortfolio",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_cas_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale: "configuration:staff_perm_cas_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING]: {
    id: PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING,
    label: "project:service_learning",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:ServiceLearningAvailability",
    moduleAvailabilityPerm: "Common:ServiceLearning",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_service_learning_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_service_learning_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT]: {
    id: PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT,
    label: "project:community_engagement",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:CommunityEngagementAvailability",
    moduleAvailabilityPerm: "Common:CommunityEngagement",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_community_engagement_coordinator_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_community_engagement_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_DP_G4_PROJECT]: {
    id: PROJECT_GROUP_TYPE_DP_G4_PROJECT,
    label: "project:group_4_project",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:Group4ProjectAvailability",
    moduleAvailabilityPerm: "Common:Group4AndCollaborativeSciences",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_g4_project_admin_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_g4_project_teacher_access_desc",
      },
    },
  },
  [PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT]: {
    id: PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT,
    label: "project:dp_collaborative_sciences_project",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:CollaborativeSciencesProjectAvailability",
    moduleAvailabilityPerm: "Common:Group4AndCollaborativeSciences",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_collaborative_sciences_project_admin_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_collaborative_sciences_project_teacher_access_desc",
      },
    },
  },
  SCHOOL_SETUP: {
    id: "SCHOOL_SETUP",
    label: "common:school_setup",
    default_value: ACCESS_LEVELS.NO_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:SchoolSetupAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_school_setup_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_school_setup_no_access_desc",
      },
    },
  },
  STAFF_PERMISSIONS: {
    id: "STAFF_PERMISSIONS",
    label: "common:staff_permissions",
    default_value: ACCESS_LEVELS.NO_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:StaffPermissionsAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_staff_permissions_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_staff_permissions_no_access_desc",
      },
    },
  },
  SCHOOL_CALENDAR: {
    id: "SCHOOL_CALENDAR",
    label: "configuration:calendar",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:SchoolCalendarAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_school_calendar_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale: {
          [CURRICULUM_TYPE_PYP]:
            "configuration:staff_perm_school_calendar_teacher_access_pyp_desc",
          [CURRICULUM_TYPE_MYP]:
            "configuration:staff_perm_school_calendar_teacher_access_desc",
          [CURRICULUM_TYPE_DP]:
            "configuration:staff_perm_school_calendar_teacher_access_desc",
          [CURRICULUM_TYPE_UBD]:
            "configuration:staff_perm_school_calendar_teacher_access_desc",
        },
        descriptionLocaleProps: ["curriculum"],
      },
    },
  },
  ACADEMIC_SETUP: {
    id: "ACADEMIC_SETUP",
    label: "teacherHomePage:academic_setup",
    default_value: ACCESS_LEVELS.NO_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:AcademicSetupAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_school_academic_setup_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_school_academic_setup_no_access_desc",
      },
    },
  },
  EDUCATOR_CENTER: {
    id: "EDUCATOR_CENTER",
    label: "configuration:school_policies_and_resources",
    default_value: ACCESS_LEVELS.NO_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:EducatorCenterAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_school_educator_center_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_school_educator_center_no_access_desc",
      },
    },
  },
  UNIT_PLANNING: {
    id: "UNIT_PLANNING",
    label: "unitPlan:staff_perm_units_of_inquiry",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:UnitPlanningAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_unit_planning_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_unit_planning_teacher_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
    },
  },
  CURRICULUM_SETTINGS: {
    id: "CURRICULUM_SETTINGS",
    label: "configuration:curriculum_setting",
    default_value: ACCESS_LEVELS.NO_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:CurriculumSettingsAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_curriculum_settings_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_curriculum_settings_no_access_desc",
      },
    },
  },
  QUESTION_LIBRARY: {
    id: "QUESTION_LIBRARY",
    label: "configuration:admin_question_library",
    default_value: ACCESS_LEVELS.NO_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:QuestionLibraryAvailability",
    moduleAvailabilityPerm: "AdminPortal:QuestionLibrary",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_question_library_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      CREATOR_ACCESS: {
        ...ACCESS_LEVELS.CREATOR_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_question_library_creator_access_desc",
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_question_library_no_access_desc",
      },
    },
  },
  PLANNING_INSIGHTS: {
    id: "PLANNING_INSIGHTS",
    label: "common:planning_insights",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:PlanningInsightsAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_planning_insights_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_planning_insights_teacher_access_desc",
      },
    },
  },
  YEAR_GROUP_MANAGER: {
    id: "YEAR_GROUP_MANAGER",
    label: "teacherHomePage:year_group_manager_small",
    default_value: ACCESS_LEVELS.NO_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:YearGroupManagerAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_year_group_manager_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_year_group_manager_no_access_desc",
      },
    },
  },
  CLASS_SUMMARY: {
    id: "CLASS_SUMMARY",
    label: "teacherHomePage:staff_perm_class_summary",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:ClassSummaryAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_class_summary_admin_access_desc",
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_class_summary_teacher_access_desc",
      },
    },
  },
  IBIS_REGISTRATION: {
    id: "IBIS_REGISTRATION",
    label: "common:ibis_registration",
    default_value: ACCESS_LEVELS.NO_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:IBISRegistrationAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_ibis_registration_admin_access_desc",
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_ibis_registration_no_access_desc",
      },
    },
  },
  IB_ECOURSEWORK: {
    id: "IB_ECOURSEWORK",
    label: "teacherHomePage:ib_ecoursework",
    default_value: ACCESS_LEVELS.NO_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:IBeCourseworkAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_ib_ecoursework_admin_access_desc",
      },
      NO_ACCESS: {
        ...ACCESS_LEVELS.NO_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_ib_ecoursework_no_access_desc",
      },
    },
  },
  GRADEBOOK: {
    id: "GRADEBOOK",
    label: "common:gradebook",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:GradebookAvailability",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_admin_gradebook_admin_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_admin_gradebook_teacher_access_desc",
        descriptionLocaleProps: ["curriculum"],
      },
    },
  },
  BEHAVIOUR_MANAGEMENT: {
    id: "BEHAVIOUR_MANAGEMENT",
    label: "behaviour:behaviour_management",
    default_value: ACCESS_LEVELS.TEACHER_ACCESS.id,
    highest_value: ACCESS_LEVELS.ADMIN_ACCESS.id,
    perm: "StaffPermission:BehaviourManagementAvailability",
    modulePerm: "FeatureFlag:BehaviourManagement",
    values: {
      ADMIN_ACCESS: {
        ...ACCESS_LEVELS.ADMIN_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_behaviour_admin_access_desc",
        descriptionLocaleProps: [
          "curriculum",
          "level",
          "action",
          "incident",
          "module",
        ],
        getDescriptionLocale: ACLStore => {
          return ACLStore.can("BehaviourAdminIncidentsVisibility")
            ? "configuration:staff_perm_behaviour_module_admin_access_desc"
            : "configuration:staff_perm_behaviour_admin_access_desc";
        },
      },
      TEACHER_ACCESS: {
        ...ACCESS_LEVELS.TEACHER_ACCESS,
        descriptionLocale:
          "configuration:staff_perm_behaviour_staff_access_desc",
        descriptionLocaleProps: ["curriculum", "incident"],
      },
    },
    shouldHideModuleColumn: ACLStore => {
      return !ACLStore.can("Behaviour:ShowInStaffPermissions");
    },
  },
};

export const STAFF_PERMISSIONS_CONFIGURATIONS = {
  FEATURE_FLAG: "StaffPermission:FeatureFlag",
};

export const MODULE_CONFIGURATION_MAP = {
  [MODULES.ATTENDANCE.id]: "StaffPermission:Attendance",
  [MODULES.ANNOUNCEMENTS.id]: "StaffPermission:Announcement",
  [MODULES.EVALUATION_CYCLES.id]: "StaffPermission:EvaluationCycles",
  [MODULES.STUDENT_PROFILE.id]: "StaffPermission:StudentProfile",
  [MODULES.PROGRESS_REPORTS.id]: "StaffPermission:ProgressReports",
  [MODULES.CLASS_SETTINGS.id]: "StaffPermission:ClassSettings",
  [MODULES.FAMILY_COMMUNICATION.id]: "StaffPermission:FamilyMessaging",
  [MODULES[PROJECT_GROUP_TYPE_PERSONAL].id]: "StaffPermission:PersonalProject",
  [MODULES[PROJECT_GROUP_TYPE_COMMUNITY].id]:
    "StaffPermission:CommunityProject",
  [MODULES[PROJECT_GROUP_TYPE_SERVICE_AS_ACTON].id]:
    "StaffPermission:ServiceAsAction",
  [MODULES[PROJECT_GROUP_TYPE_DP_EE].id]: "StaffPermission:ExtendedEssay",
  [MODULES[PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT].id]:
    "StaffPermission:ReflectiveProject",
  [MODULES[PROJECT_GROUP_TYPE_DP_TOK_ESSAY].id]: "StaffPermission:TOKEssay",
  [MODULES[PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION].id]:
    "StaffPermission:TOKExhibition",
  [MODULES[PROJECT_GROUP_TYPE_DP_CAS].id]:
    "StaffPermission:CreativityActivityService",
  [MODULES[PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING].id]:
    "StaffPermission:ServiceLearning",
  [MODULES[PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT].id]:
    "StaffPermission:CommunityEngagement",
  [MODULES[PROJECT_GROUP_TYPE_DP_G4_PROJECT].id]:
    "StaffPermission:Group4Project",
  [MODULES[PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT].id]:
    "StaffPermission:CollaborativeSciencesProject",
  [MODULES.STAFF_PERMISSIONS.id]: "StaffPermission:StaffPermissions",
  [MODULES.SCHOOL_CALENDAR.id]: "StaffPermission:SchoolCalendar",
  [MODULES.ACADEMIC_SETUP.id]: "StaffPermission:AcademicSetup",
  [MODULES.EDUCATOR_CENTER.id]: "StaffPermission:EducatorCenter",
  [MODULES.CURRICULUM_SETTINGS.id]: "StaffPermission:CurriculumSettings",
  [MODULES.PLANNING_INSIGHTS.id]: "StaffPermission:PlanningInsights",
  [MODULES.CLASS_SUMMARY.id]: "StaffPermission:ClassSummary",
  [MODULES.IBIS_REGISTRATION.id]: "StaffPermission:IBISRegistration",
  [MODULES.IB_ECOURSEWORK.id]: "StaffPermission:IBeCoursework",
  [MODULES.SCHOOL_SETUP.id]: "StaffPermission:SchoolSetup",
  [MODULES.UNIT_PLANNING.id]: "StaffPermission:UnitPlanning",
  [MODULES.QUESTION_LIBRARY.id]: "StaffPermission:QuestionLibrary",
  [MODULES.GRADEBOOK.id]: "StaffPermission:Gradebook",
  [MODULES.BEHAVIOUR_MANAGEMENT.id]: "StaffPermission:BehaviourManagement",
};

export const MODULE_PERMS = _.reduce(
  MODULES,
  (result, module) => {
    if (module.perm) {
      result.push(module.perm);
    }
    if (module.modulePerm) {
      result.push(module.modulePerm);
    }
    if (module.moduleAvailabilityPerm) {
      result.push(module.moduleAvailabilityPerm);
    }
    return result;
  },
  [..._.values(MODULE_CONFIGURATION_MAP)]
);
export const CONFIG_STAFF_PERM_MAP = {
  "TeacherPortal:AttendanceManager": {
    staffPermissionConfiguration:
      MODULE_CONFIGURATION_MAP[MODULES.ATTENDANCE.id],
    allowedAccess: [
      ACCESS_LEVELS.ADMIN_ACCESS.id,
      ACCESS_LEVELS.TEACHER_ACCESS.id,
    ],
  },
  "Common:AttendanceDashboard": {
    staffPermissionConfiguration:
      MODULE_CONFIGURATION_MAP[MODULES.ATTENDANCE.id],
    allowedAccess: [
      ACCESS_LEVELS.ADMIN_ACCESS.id,
      ACCESS_LEVELS.TEACHER_ACCESS.id,
    ],
  },
  "FeatureFlag:AllowAnnouncementCreation": {
    staffPermissionConfiguration:
      MODULE_CONFIGURATION_MAP[MODULES.ANNOUNCEMENTS.id],
    allowedAccess: [
      ACCESS_LEVELS.ADMIN_ACCESS.id,
      ACCESS_LEVELS.TEACHER_ACCESS.id,
    ],
  },
};

export const CURRICULUM_MODULE_MAP = {
  IB_PYP: {
    id: "IB_PYP",
    modules: [
      MODULES.ANNOUNCEMENTS.id,
      MODULES.EVALUATION_CYCLES.id,
      MODULES.FAMILY_COMMUNICATION.id,
      MODULES.ATTENDANCE.id,
      MODULES.PROGRESS_REPORTS.id,
      MODULES.STUDENT_PROFILE.id,
      // MODULES.CLASS_SETTINGS.id,
      MODULES.SCHOOL_SETUP.id,
      MODULES.STAFF_PERMISSIONS.id,
      MODULES.SCHOOL_CALENDAR.id,
      MODULES.ACADEMIC_SETUP.id,
      MODULES.EDUCATOR_CENTER.id,
      MODULES.UNIT_PLANNING.id,
      MODULES.CURRICULUM_SETTINGS.id,
      MODULES.PLANNING_INSIGHTS.id,
      MODULES.QUESTION_LIBRARY.id,
      MODULES.GRADEBOOK.id,
      MODULES.BEHAVIOUR_MANAGEMENT.id,
    ],
  },
  IB_MYP: {
    id: "IB_MYP",
    modules: [
      MODULES.ANNOUNCEMENTS.id,
      MODULES.EVALUATION_CYCLES.id,
      MODULES.FAMILY_COMMUNICATION.id,
      MODULES.ATTENDANCE.id,
      MODULES.PROGRESS_REPORTS.id,
      MODULES.STUDENT_PROFILE.id,
      MODULES[PROJECT_GROUP_TYPE_PERSONAL].id,
      MODULES[PROJECT_GROUP_TYPE_COMMUNITY].id,
      MODULES[PROJECT_GROUP_TYPE_SERVICE_AS_ACTON].id,
      MODULES.SCHOOL_SETUP.id,
      MODULES.STAFF_PERMISSIONS.id,
      MODULES.SCHOOL_CALENDAR.id,
      MODULES.ACADEMIC_SETUP.id,
      MODULES.EDUCATOR_CENTER.id,
      MODULES.UNIT_PLANNING.id,
      MODULES.CURRICULUM_SETTINGS.id,
      MODULES.PLANNING_INSIGHTS.id,
      MODULES.CLASS_SUMMARY.id,
      MODULES.QUESTION_LIBRARY.id,
      MODULES.GRADEBOOK.id,
      MODULES.BEHAVIOUR_MANAGEMENT.id,
    ],
  },
  UBD: {
    id: "UBD",
    modules: [
      MODULES.ANNOUNCEMENTS.id,
      MODULES.EVALUATION_CYCLES.id,
      MODULES.FAMILY_COMMUNICATION.id,
      MODULES.ATTENDANCE.id,
      MODULES.PROGRESS_REPORTS.id,
      MODULES.STUDENT_PROFILE.id,
      MODULES.SCHOOL_SETUP.id,
      MODULES.STAFF_PERMISSIONS.id,
      MODULES.SCHOOL_CALENDAR.id,
      MODULES.ACADEMIC_SETUP.id,
      MODULES.EDUCATOR_CENTER.id,
      MODULES.UNIT_PLANNING.id,
      MODULES.CURRICULUM_SETTINGS.id,
      MODULES.PLANNING_INSIGHTS.id,
      MODULES.QUESTION_LIBRARY.id,
      MODULES.GRADEBOOK.id,
      MODULES.BEHAVIOUR_MANAGEMENT.id,
    ],
  },
  IB_DP: {
    id: "IB_DP",
    modules: [
      MODULES.ANNOUNCEMENTS.id,
      MODULES.EVALUATION_CYCLES.id,
      MODULES.FAMILY_COMMUNICATION.id,
      MODULES.ATTENDANCE.id,
      MODULES.PROGRESS_REPORTS.id,
      MODULES.STUDENT_PROFILE.id,
      MODULES[PROJECT_GROUP_TYPE_DP_EE].id,
      MODULES[PROJECT_GROUP_TYPE_DP_TOK_ESSAY].id,
      MODULES[PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION].id,
      MODULES[PROJECT_GROUP_TYPE_DP_CAS].id,
      MODULES[PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT].id,
      MODULES[PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING].id,
      MODULES[PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT].id,
      MODULES[PROJECT_GROUP_TYPE_DP_G4_PROJECT].id,
      MODULES[PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT].id,
      MODULES.SCHOOL_SETUP.id,
      MODULES.STAFF_PERMISSIONS.id,
      MODULES.SCHOOL_CALENDAR.id,
      MODULES.ACADEMIC_SETUP.id,
      MODULES.EDUCATOR_CENTER.id,
      MODULES.UNIT_PLANNING.id,
      MODULES.CURRICULUM_SETTINGS.id,
      MODULES.PLANNING_INSIGHTS.id,
      MODULES.IBIS_REGISTRATION.id,
      MODULES.IB_ECOURSEWORK.id,
      MODULES.QUESTION_LIBRARY.id,
      MODULES.GRADEBOOK.id,
      MODULES.BEHAVIOUR_MANAGEMENT.id,
    ],
  },
};
